import { useIntercom } from "react-use-intercom";

import AgencyPurchaseAlertBanners from "features/subscriptions/components/ManageSubscription/AgencyPurchaseAlertBanners";
import { PRICING_PLANS } from "features/subscriptions/constants";
import { useManageSubscription } from "features/subscriptions/context/ManageSubscriptionContext";

import Alert from "common/components/ui/Alert";
import { Button } from "common/components/ui/Button";
import { formatShortDate } from "common/helpers/utils";

interface AlertBannersProps {
  assignedLicenseCount: number;
}

export default function AlertBanners({
  assignedLicenseCount,
}: AlertBannersProps) {
  const { showNewMessage } = useIntercom();
  const {
    hasExistingSubscription,
    hasActiveSubscription,
    subscriptionType,
    initialState,
    isPricingPeriodDowngrade,
    isDowngrade,
    isCancellation,
    seatQuantity,
    pricing,
  } = useManageSubscription();

  const isAgencyPurchase =
    subscriptionType === "agency" && !hasActiveSubscription;

  if (hasExistingSubscription) {
    const { status, next_billing } = initialState.subscription;

    if (status === "in_trial" && pricing?.plan.period_unit !== "year") {
      return (
        <Alert variant="gradient">
          You will not be charged until your trial expires. Your subscription
          will automatically upgrade to a paid plan on{" "}
          {formatShortDate(next_billing * 1000)}
        </Alert>
      );
    }

    if (status !== "cancelled") {
      if (subscriptionType === "agency" && isPricingPeriodDowngrade) {
        const planName = PRICING_PLANS[subscriptionType].name;
        return (
          <Alert variant="warning">
            <div className="flex flex-col gap-y-1.5">
              <span>
                The {planName} cannot be downgraded through the dashboard,
                please contact our team to downgrade your subscription
              </span>
              <Button
                variant="secondary-black"
                size="sm"
                className="ml-auto"
                onClick={() => {
                  showNewMessage(
                    `Hello, i would like to downgrade my ${planName} subscription`,
                  );
                }}
              >
                Contact support
              </Button>
            </div>
          </Alert>
        );
      } else if (isCancellation && subscriptionType === "user") {
        return (
          <Alert variant="warning">
            You are about to cancel your subscription. The cancellation will
            take place at the end of your current billing period
          </Alert>
        );
      } else if (isDowngrade || isPricingPeriodDowngrade) {
        if (subscriptionType === "agency") {
          if (seatQuantity < assignedLicenseCount) {
            return (
              <Alert variant="warning">
                Cannot downgrade subscription while seats are in use. Unassign
                the seats you want to remove, then try again
              </Alert>
            );
          }
        }
        return (
          <Alert variant="warning">
            This is a scheduled change. Your subscription will be updated on the
            next billing date
          </Alert>
        );
      }
    }
  }

  if (isAgencyPurchase) {
    return <AgencyPurchaseAlertBanners />;
  }
}
